/**
 * Shop Admin Page
 * Product management dashboard for admin users
 */

import React, { useState, useEffect } from 'react';
import { Container, Main, Flex } from '../../../components/styled/Layout';
import { Card, CardContent } from '../../../components/styled/Card';
import { PrimaryButton, SecondaryButton, DangerButton } from '../../../components/styled/Button';
import apiService from '../../../services/apiService';
import './Shop.css';

const ShopAdmin = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    image: '',
    stockQty: '',
    variations: '[]',
    specs: '{}'
  });

  useEffect(() => {
    if (activeTab === 'products') {
      loadProducts();
    } else if (activeTab === 'categories') {
      loadCategories();
    } else if (activeTab === 'orders') {
      loadOrders();
    }
  }, [activeTab]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/shop/products?limit=100');
      setProducts(response.items || []);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/shop/categories');
      setCategories(response || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/shop/admin/orders');
      setOrders(response || []);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = () => {
    setEditingProduct(null);
    setProductForm({
      name: '',
      description: '',
      price: '',
      category: '',
      image: '',
      stockQty: '',
      variations: '[]',
      specs: '{}'
    });
    setShowProductForm(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || '',
      description: product.description || '',
      price: String(product.price || ''),
      category: product.category || '',
      image: product.image || '',
      stockQty: String(product.stockQty || ''),
      variations: typeof product.variations === 'string' ? product.variations : JSON.stringify(product.variations || []),
      specs: typeof product.specs === 'string' ? product.specs : JSON.stringify(product.specs || {})
    });
    setShowProductForm(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    
    try {
      const productData = {
        ...productForm,
        price: parseFloat(productForm.price),
        stockQty: parseInt(productForm.stockQty),
        variations: JSON.parse(productForm.variations),
        specs: JSON.parse(productForm.specs)
      };

      if (editingProduct) {
        await apiService.put(`/shop/admin/products/${editingProduct.id}`, productData);
        alert('Product updated successfully!');
      } else {
        await apiService.post('/shop/admin/products', productData);
        alert('Product created successfully!');
      }

      setShowProductForm(false);
      loadProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Failed to save product: ' + error.message);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) return;

    try {
      await apiService.delete(`/shop/admin/products/${productId}`);
      alert('Product deleted successfully!');
      loadProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product');
    }
  };

  return (
    <Container>
      <Main>
        <div className="shop-page admin-page">
          <h1>Shop Admin</h1>

          {/* Tabs */}
          <div className="admin-tabs">
            <button
              className={`admin-tab ${activeTab === 'products' ? 'active' : ''}`}
              onClick={() => setActiveTab('products')}
            >
              Products
            </button>
            <button
              className={`admin-tab ${activeTab === 'categories' ? 'active' : ''}`}
              onClick={() => setActiveTab('categories')}
            >
              Categories
            </button>
            <button
              className={`admin-tab ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              All Orders
            </button>
          </div>

          {/* Products Tab */}
          {activeTab === 'products' && (
            <div className="admin-content">
              {!showProductForm ? (
                <>
                  <Flex justify="space-between" align="center" style={{ marginBottom: '20px' }}>
                    <h2>Products ({products.length})</h2>
                    <PrimaryButton onClick={handleCreateProduct}>
                      + Add Product
                    </PrimaryButton>
                  </Flex>

                  {loading ? (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                      <p>Loading products...</p>
                    </div>
                  ) : (
                    <div className="admin-table-container">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Image</th>
                            <th>Name</th>
                            <th>Price</th>
                            <th>Stock</th>
                            <th>Category</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map(product => (
                            <tr key={product.id}>
                              <td>
                                <img src={product.image} alt={product.name} style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
                              </td>
                              <td>{product.name}</td>
                              <td>${product.price?.toFixed(2)}</td>
                              <td>
                                <span className={`stock-badge ${product.stockQty > 0 ? 'in-stock' : 'out-of-stock'}`}>
                                  {product.stockQty || 0}
                                </span>
                              </td>
                              <td>{product.category}</td>
                              <td>
                                <Flex gap="8px">
                                  <SecondaryButton size="small" onClick={() => handleEditProduct(product)}>
                                    Edit
                                  </SecondaryButton>
                                  <DangerButton size="small" onClick={() => handleDeleteProduct(product.id)}>
                                    Delete
                                  </DangerButton>
                                </Flex>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                <Card>
                  <CardContent>
                    <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
                    <form onSubmit={handleSaveProduct} className="admin-form">
                      <div className="form-group">
                        <label>Product Name *</label>
                        <input
                          type="text"
                          value={productForm.name}
                          onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                          required
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label>Description *</label>
                        <textarea
                          value={productForm.description}
                          onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                          required
                          rows="4"
                          className="form-input"
                        />
                      </div>

                      <Flex gap="12px">
                        <div className="form-group" style={{ flex: 1 }}>
                          <label>Price *</label>
                          <input
                            type="number"
                            step="0.01"
                            value={productForm.price}
                            onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                            required
                            className="form-input"
                          />
                        </div>

                        <div className="form-group" style={{ flex: 1 }}>
                          <label>Stock Quantity *</label>
                          <input
                            type="number"
                            value={productForm.stockQty}
                            onChange={(e) => setProductForm({ ...productForm, stockQty: e.target.value })}
                            required
                            className="form-input"
                          />
                        </div>
                      </Flex>

                      <div className="form-group">
                        <label>Category *</label>
                        <input
                          type="text"
                          value={productForm.category}
                          onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                          required
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label>Image URL *</label>
                        <input
                          type="url"
                          value={productForm.image}
                          onChange={(e) => setProductForm({ ...productForm, image: e.target.value })}
                          required
                          className="form-input"
                          placeholder="https://example.com/image.jpg"
                        />
                        {productForm.image && (
                          <img src={productForm.image} alt="Preview" style={{ maxWidth: '200px', marginTop: '8px' }} />
                        )}
                      </div>

                      <div className="form-group">
                        <label>Variations (JSON Array)</label>
                        <textarea
                          value={productForm.variations}
                          onChange={(e) => setProductForm({ ...productForm, variations: e.target.value })}
                          rows="3"
                          className="form-input"
                          placeholder='[{"name":"Size","options":["S","M","L"]}]'
                        />
                      </div>

                      <div className="form-group">
                        <label>Specifications (JSON Object)</label>
                        <textarea
                          value={productForm.specs}
                          onChange={(e) => setProductForm({ ...productForm, specs: e.target.value })}
                          rows="3"
                          className="form-input"
                          placeholder='{"Brand":"Example","Material":"Cotton"}'
                        />
                      </div>

                      <Flex gap="12px" style={{ marginTop: '24px' }}>
                        <SecondaryButton onClick={() => setShowProductForm(false)} style={{ flex: 1 }}>
                          Cancel
                        </SecondaryButton>
                        <PrimaryButton type="submit" style={{ flex: 1 }}>
                          {editingProduct ? 'Update Product' : 'Create Product'}
                        </PrimaryButton>
                      </Flex>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Categories Tab */}
          {activeTab === 'categories' && (
            <div className="admin-content">
              <h2>Categories ({categories.length})</h2>
              {loading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                </div>
              ) : (
                <div className="categories-grid">
                  {categories.map(cat => (
                    <Card key={cat.id}>
                      <CardContent>
                        <h3>{cat.name}</h3>
                        <p>{cat.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="admin-content">
              <h2>All Orders ({orders.length})</h2>
              {loading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                </div>
              ) : orders.length === 0 ? (
                <div className="empty-state">
                  <p>No orders yet</p>
                </div>
              ) : (
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Customer</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map(order => (
                        <tr key={order.id}>
                          <td>{order.displayOrderId || order.id.substring(0, 8)}</td>
                          <td>{order.userId}</td>
                          <td>${order.total?.toFixed(2)}</td>
                          <td>
                            <span className={`status-badge status-${order.status}`}>
                              {order.status}
                            </span>
                          </td>
                          <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </Main>
    </Container>
  );
};

export default ShopAdmin;

